import type { AxiosError } from 'axios'
import { FormValueScope } from '@epages/react-components'
import type { Ref } from 'react'
import { fromJS } from 'immutable'
import { v4 as generateUUID } from 'uuid'
import { useEffect, useRef, useState } from 'react'
import { useStore } from 'react-redux'

import { allowedMimeTypes, imageUrl, storeFile } from '../templateComponents/Workspace/daliConfig'
import { getErrorReasonMessage } from '../templateComponents/Workspace/plugins/image/ImageField'
import { useTranslate } from '../../utils/translate'
import CustomLogo from './CustomLogo'
import FormValue from '../formComponents/FormValue'
import Logo from './Logo'
import useHelpCenterLink from '../../utils/hooks/useHelpCenterLink'

const customLogo: ImmutableMap = fromJS({ active: true, title: '', id: 'custom', path: '' })

type Props = {
  scope: string
  updatePopoverPosition: VoidFunction
  fieldsetRef: Ref<HTMLFieldSetElement>
}

export default function LogoCollection({ scope, updatePopoverPosition, fieldsetRef }: Readonly<Props>) {
  const store: any = useStore()
  const [error, setError] = useState<AxiosError<{ message: string }> | null>(null)
  const helpCenterUrl = useHelpCenterLink('IMAGE_FORMATS')
  const isUnsupportedMediaType = error?.response?.status === 415

  const uploadRef = useRef<HTMLInputElement>(null)
  const itemsContainerRef = useRef<HTMLDivElement | null>(undefined)

  const t = useTranslate('interface')
  const tScoped = (subKey: string, ...args: any[]) =>
    t(`components.bottomBar.footerSettings.sections.popovers.logoSelection.${subKey}`, ...args)

  useEffect(() => {
    if (error) updatePopoverPosition()
  }, [error, updatePopoverPosition])

  const renderLogos = (logos: ImmutableList, setLogos: (logos: ImmutableList) => void) => {
    const defaultLogos = logos.filter((logo: ImmutableMap) => !logo.has('path'))
    const customLogos = logos.filter((logo: ImmutableMap) => logo.has('path'))

    const removeCustomLogo = (index: number) => {
      setError(null)
      setLogos(logos.remove(index))
    }

    return (
      <FormValueScope name={scope}>
        <>
          {defaultLogos
            .map((logo: ImmutableMap, index) => (
              <Logo t={tScoped} logo={logo} index={logos.indexOf(logo)} key={index} />
            ))
            .toArray()}
          {customLogos
            .map((logo: ImmutableMap, index) => (
              <FormValueScope key={index} name={logos.indexOf(logo)}>
                <CustomLogo
                  logo={logo}
                  t={tScoped}
                  onUploadSuccess={() => error && setError(null)}
                  onClick={() => removeCustomLogo(logos.indexOf(logo))}
                  onError={(error) => setError(error)}
                />
              </FormValueScope>
            ))
            .toArray()}
        </>
      </FormValueScope>
    )
  }

  const renderUploadButton = (logos: ImmutableList, setLogos: (logos: ImmutableList) => void) => {
    const addCustomLogo = (filePath: string) =>
      setLogos(logos.push(customLogo.set('path', filePath).set('id', 'custom-' + generateUUID())))

    const upload = storeFile(store.api)
    const handleUpload = (file: File) => {
      upload(file).then(
        (response: Storage.Image) => {
          setError(null)
          addCustomLogo(imageUrl(store.getState().getIn(['shop', 'slug']))(response.absoluteDownloadUrl))

          // Scroll the uploaded image into view
          if (itemsContainerRef.current) itemsContainerRef.current.scrollTop = itemsContainerRef.current.scrollHeight
        },
        (error) => setError(error),
      )
    }

    return (
      <div>
        <button type="button" className="ep-custom-settings-add" onClick={() => uploadRef.current?.click()}>
          {tScoped('customLogos.createButton.label')}
        </button>
        <input
          ref={uploadRef}
          type="file"
          aria-label={tScoped('logoUpload.label')}
          accept={allowedMimeTypes.join(',')}
          onChange={({ target }) => target.files && handleUpload(target.files[0])}
          value=""
        />
      </div>
    )
  }

  return (
    <>
      {error && (
        <div className="dali-settingslayer-element">
          <div className="dali-notification-danger">
            {t('components.imageUploadComponent.imageField.errorMessages.messageWithoutOldImage', {
              reason: getErrorReasonMessage(error, t),
            })}
            {isUnsupportedMediaType && (
              <a
                href={helpCenterUrl}
                target="_blank"
                className="ep-form-row-text-external-link"
                rel="noreferrer noopener"
              >
                {t('components.imageUploadComponent.imageField.errorMessages.helpCenterLink')}
              </a>
            )}
          </div>
        </div>
      )}
      <fieldset ref={fieldsetRef}>
        <legend>{tScoped('title')}</legend>
        <scroll-shadow>
          <div
            ref={(node) => {
              itemsContainerRef.current = node
            }}
            className="ep-footer-sections-settings-popover-column"
          >
            <FormValue name={scope}>{renderLogos}</FormValue>
          </div>
        </scroll-shadow>
      </fieldset>
      <FormValue name={scope}>{renderUploadButton}</FormValue>
    </>
  )
}
